import { Badge, Popover, Stack, Text } from "@mantine/core";
import { IconHistory } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useMemo, useState } from "react";
import dayjs from "dayjs";
import { casePathologistAssignmentQueryKeys } from "../../queryKeys/casePathologistAssignmentQueryKeys";
import { casesService } from "../../services/cases.service";

interface AssignmentHistoryTransferContextProps {
  caseId: string;
  pathologistId: string;
  recordedWorkGuidance: string;
}

/**
 * Reassignment context, collapsed behind a header badge.
 *
 * The transfer detail matters once — unsaved work from the previous pathologist
 * did not come across — but it never expires, so as a full-width banner it sat
 * permanently above the header pushing the case down the page. It now reads as a
 * badge alongside the other case-state badges and opens on click.
 */
export default function AssignmentHistoryTransferContext({
  caseId,
  pathologistId,
  recordedWorkGuidance,
}: AssignmentHistoryTransferContextProps) {
  const [opened, setOpened] = useState(false);

  const historyQuery = useQuery({
    queryKey: casePathologistAssignmentQueryKeys.latestHistory(caseId),
    queryFn: () =>
      casesService.getCasePathologistAssignmentHistory(caseId, 1, 1),
    staleTime: 0,
    refetchOnMount: "always",
  });

  const latestTransfer = useMemo(
    () => historyQuery.data?.data[0],
    [historyQuery.data?.data]
  );
  const isIncomingPathologist = useMemo(
    () => latestTransfer?.toPathologist.id === pathologistId,
    [latestTransfer?.toPathologist.id, pathologistId]
  );
  const occurredAtLabel = useMemo(
    () =>
      latestTransfer
        ? dayjs(latestTransfer.occurredAt).format("MMM D, YYYY h:mm A")
        : "",
    [latestTransfer]
  );

  const handleToggle = useCallback(() => setOpened((isOpen) => !isOpen), []);

  if (!latestTransfer || !isIncomingPathologist) return <></>;

  return (
    <Popover
      opened={opened}
      onChange={setOpened}
      position="bottom-start"
      offset={8}
      width={340}
      shadow="md"
      withinPortal
      withArrow
    >
      <Popover.Target>
        <Badge
          component="button"
          type="button"
          size="sm"
          color="blue"
          variant="light"
          radius="sm"
          leftSection={<IconHistory size={10} />}
          onClick={handleToggle}
          style={{ cursor: "pointer" }}
        >
          REASSIGNED TO YOU
        </Badge>
      </Popover.Target>

      <Popover.Dropdown>
        <Stack
          gap={4}
          role="status"
          aria-label="Recorded work from before reassignment"
        >
          <Text size="sm" fw={600}>
            Recorded work from before reassignment
          </Text>
          <Text size="sm">{recordedWorkGuidance}</Text>
          <Text size="sm">
            Reassigned from {latestTransfer.fromPathologist.fullName} on{" "}
            {occurredAtLabel}.
          </Text>
          <Text size="sm">Reason: {latestTransfer.reason}</Text>
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
}
